import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import InfoOutline from '@material-ui/icons/InfoOutlined';
import Check from '@material-ui/icons/Check';
import Warning from '@material-ui/icons/Warning';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import SnackbarContent from '../modules/material-ui-kit/Snackbar/Snackbar';
import { zIndexConfig } from '../constants/StyleConstants';

const NotificationContext = React.createContext({
    showNotification: () => {},
    showSuccessNotification: () => {},
    showInfoNotification: () => {},
    showWarnNotification: () => {},
    showErrorNotification: () => {},
});

export const NotificationProvider = NotificationContext.Provider;

export const NotificationConsumer = NotificationContext.Consumer;

let currentNotificationId = 1;
export const DEFAULT_VISIBILITY_TIME = 3000;
const HIDE_TRANSITION_TIME = 1000;

const styles = {
    container: {
        width: '100%',
        maxWidth: '100%',
        position: 'fixed',
        paddingLeft: 0,
        zIndex: zIndexConfig.notification,
    },
    root: {
        width: '60%',
        left: -8,
    },
};

const useStyles = makeStyles(() => styles);

export const getColorByType = (type) => {
    switch (type) {
        case 'success':
            return 'success';
        case 'warn':
            return 'warning';
        case 'error':
            return 'danger';
        case 'info':
            return 'info';
        default:
            return InfoOutline;
    }
};

export const getIconType = (type) => {
    switch (type) {
        case 'warn':
            return Warning;
        case 'error':
            return ErrorOutline;
        case 'success':
            return Check;
        case 'info':
        default:
            return InfoOutline;
    }
};

const NotificationPlaceholder = ({ onClose, notifications }) => {
    const { t } = useTranslation();
    const classes = useStyles();

    return (notifications || []).map(({
        id, type, open, message, params,
    }) => {
        const color = getColorByType(type);
        const icon = getIconType(type);

        return (
            <SnackbarContent
                key={id}
                classes={classes}
                message={t(message, params)}
                closeNotification={() => onClose(id)}
                color={color}
                icon={icon}
                open={open}
                place='tc'
            />
        );
    });
};

export const AppWithNotification = ({ children }) => {
    const { i18n } = useTranslation();
    const [notifications, setNotifications] = useState([]);

    const removeNotification = (notificationId) => {
        setNotifications((currentNotifications) => (currentNotifications || []).filter(({ id }) => id !== notificationId));
    };

    const hideNotification = (notificationId) => {
        setNotifications((currentNotifications) => {
            const index = (currentNotifications || []).findIndex(({ id }) => id === notificationId);

            if (index > -1) {
                return ((currentNotifications || []).map((currentNotification) => {
                    if (currentNotification.id === notificationId) {
                        return {
                            ...currentNotification,
                            open: false,
                        };
                    }

                    return currentNotification;
                }));
            }

            return currentNotifications;
        });

        setTimeout(() => removeNotification(notificationId), HIDE_TRANSITION_TIME);
    };

    const showNotification = (notification) => {
        // eslint-disable-next-line no-plusplus
        const id = currentNotificationId++;

        setNotifications((currentNotifications) => ([...currentNotifications, { ...notification, open: true, id }]));

        setTimeout(() => hideNotification(id), notification.time || DEFAULT_VISIBILITY_TIME);

        return id;
    };

    const showSuccessNotification = (message) => showNotification({
        message,
        type: 'success',
    });

    const showInfoNotification = (message) => showNotification({
        message,
        type: 'info',
    });

    const showWarnNotification = (message) => showNotification({
        message,
        type: 'warn',
    });

    const showErrorNotification = (message, { params, defaultLabel } = {}) => {
        const isTranslationExist = message && i18n.exists(message);

        showNotification({
            message: isTranslationExist ? message : 'DEFAULT_NOTIFICATION_ERROR',
            type: 'error',
            ...(isTranslationExist && { params }),
        });
    };

    return (
        <NotificationProvider value={{
            showNotification,
            showSuccessNotification,
            showInfoNotification,
            showWarnNotification,
            showErrorNotification,
            hideNotification,
        }}>
            <NotificationPlaceholder
                notifications={notifications}
                onClose={(id) => hideNotification(id)}
            />
            {children}
        </NotificationProvider>
    );
};

export default NotificationContext;
